import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Badge,
  Button,
  Form,
  Image,
  OverlayTrigger,
  Popover,
  Spinner,
  Tooltip,
} from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import {
  faBookmark,
  faCheck,
  faCopy,
  faExternalLinkAlt,
  faObjectGroup,
  faQuestionCircle,
  faSearch,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  auroMetalSaurus,
  bootstrapDanger,
  contentVerifiedBlue,
  darkOrange,
  grape,
  greyStroke,
  lightAmber,
  lightRed,
  positiveGreen,
  shadowGrape,
  squidInk,
  warmGray,
  white,
} from 'lib/css/colors';
import { BrandVerificationStatus } from 'lib/enums/BrandVerificationStatus';
import {
  BLANK_WARNING_HINT,
  ManufacturerMode,
} from 'components/manage_brands/brand_verify/constants';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import AISuggestionPanel from 'components/manage_brands/brand_verify/AISuggestionPanel';
import DistributorsList from 'components/manage_brands/brand_verify/DistributorsList';
import ExistingManufacturerFields from 'components/manage_brands/brand_verify/ExistingManufacturerFields';
import FieldLabel from 'components/manage_brands/brand_verify/FieldLabel';
import ManufacturerPanel from 'components/manage_brands/brand_verify/ManufacturerPanel';
import pointerOnHover from 'lib/css/pointerOnHover';
import SampleProductsList from 'components/manage_brands/brand_verify/SampleProductsList';
import sharedStyles from 'components/manage_brands/brand_verify/sharedStyles';
import { handleCopyToClipboard, isBlank } from 'lib/utils/stringUtils';

const SAVED_ON_VERIFY_HINT = 'saved on verify';
const CONFIDENCE_VERIFIED_THRESHOLD = 80;

function BrandVerifyCard({
  suggestion,
  formValues,
  updateField,
  onPickExistingMfr,
  onActivatePanel,
  manufacturerUrlLoading,
  updateVerificationStatus,
  onMergeToVerified,
  onSkip,
  onBookmark,
  updatingStatus,
}) {
  const {
    eCommName,
    brandUrl,
    manufacturerMode,
    manufacturerId,
    existingManufacturerName,
    existingManufacturerUrl,
    existingManufacturerSavedName,
    existingManufacturerSavedUrl,
    newManufacturerName,
    newManufacturerUrl,
  } = formValues;

  const isExistingActive = manufacturerMode === ManufacturerMode.EXISTING;
  const isNewActive = manufacturerMode === ManufacturerMode.NEW;

  useEffect(() => {
    const isTyping = () => {
      const tag = document.activeElement?.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable;
    };
    const handler = e => {
      if (e.metaKey || e.ctrlKey || e.altKey || isTyping()) return;
      if (updatingStatus || manufacturerUrlLoading) return;
      if (e.key === 'v' || e.key === 'V') {
        e.preventDefault();
        updateVerificationStatus(BrandVerificationStatus.VERIFIED);
      } else if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        updateVerificationStatus(BrandVerificationStatus.UNVERIFIABLE);
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        onMergeToVerified();
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        onSkip();
      } else if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        onBookmark();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    updateVerificationStatus,
    onMergeToVerified,
    onSkip,
    onBookmark,
    updatingStatus,
    manufacturerUrlLoading,
  ]);

  return (
    <div className={classNames(css(sharedStyles.card), 'p-4')}>
      <div
        className={classNames(
          css(sharedStyles.cardTopRow),
          'd-flex justify-content-between align-items-start mb-3',
        )}
      >
        <div>
          <div className="d-flex align-items-center mb-2">
            <OverlayTrigger
              placement="right"
              overlay={
                <Popover id={`popover-brand-logo-${suggestion.brand.id}`} style={{ maxWidth: 400 }}>
                  <Popover.Content>
                    <Image
                      width={400}
                      fluid
                      src={suggestion.brand.logo_url || placeholderProductImage}
                      alt=""
                      onError={e => {
                        e.target.onerror = null;
                        e.target.src = placeholderProductImage;
                      }}
                    />
                  </Popover.Content>
                </Popover>
              }
            >
              <img
                src={suggestion.brand.logo_url || placeholderProductImage}
                alt={`${suggestion.brand.name} logo`}
                className={css(styles.brandLogo)}
                onError={e => {
                  e.target.onerror = null;
                  e.target.src = placeholderProductImage;
                }}
              />
            </OverlayTrigger>
            <span className={css(sharedStyles.heading)}>{suggestion.brand.name}</span>
            <Badge className={classNames(css(styles.unverifiedBadge), 'ml-2')}>
              {suggestion.brand.verification_status}
            </Badge>
            <a
              href={`/enrich-products/brand-details/${suggestion.brand.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className={css(sharedStyles.idLink, styles.brandIdSpacing)}
            >
              ID {suggestion.brand.id}
            </a>
            <a
              href={`/enrich-products/manage-brands?query=${encodeURIComponent(
                suggestion.brand.name,
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className={css(sharedStyles.idLink, styles.brandIdSpacing)}
            >
              <FontAwesomeIcon icon={faSearch} className="mr-1" />
              Search
            </a>
          </div>
        </div>
        <div className="text-right">
          <div className={css(styles.activeProductCount)}>{suggestion.brand.product_count}</div>
          <div className={css(styles.activeProductLabel)}>active products</div>
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent(suggestion.brand.name)}`}
            target="_blank"
            rel="noopener noreferrer"
            className={css(styles.googleLink)}
          >
            <FontAwesomeIcon icon={faQuestionCircle} className="mr-1" />
            Google
          </a>
        </div>
      </div>

      {(() => {
        const askClaudeResult =
          suggestion.brand_verification_suggestion_metadata?.ask_claude_result;
        const confidenceScore = suggestion.confidence_score ?? 0;

        if (!askClaudeResult) {
          return (
            <AISuggestionPanel
              title="Ask Claude result"
              status="ERROR"
              confidence={confidenceScore}
            >
              <div className="mb-2">
                No Ask Claude result is available for this suggestion. The AI analysis may not have
                run, may have failed, or the metadata is missing.
              </div>
            </AISuggestionPanel>
          );
        }

        const renderUrl = url =>
          url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={css(styles.claudeUrl)}
            >
              {url}
            </a>
          ) : (
            <span className={css(styles.claudeMetaValue)}>—</span>
          );

        const renderCopyButton = value =>
          value ? (
            <OverlayTrigger overlay={<Tooltip>Copy to clipboard</Tooltip>}>
              <FontAwesomeIcon
                icon={faCopy}
                className={classNames(pointerOnHover, css(styles.copyIcon), 'ml-2')}
                onClick={() => handleCopyToClipboard(value)}
              />
            </OverlayTrigger>
          ) : null;

        return (
          <AISuggestionPanel
            title="Ask Claude result"
            status={confidenceScore >= CONFIDENCE_VERIFIED_THRESHOLD ? 'VERIFIED' : 'NEEDS_REVIEW'}
            confidence={confidenceScore}
          >
            <div className="mb-2">{askClaudeResult.reasoning || '—'}</div>
            <div className={css(styles.claudeMetaRow)}>
              <span className={css(styles.claudeMetaLabel)}>E-Comm Name:</span>
              <span className={css(styles.claudeMetaValue)}>
                {askClaudeResult.ecomm_name || '—'}
              </span>
              {renderCopyButton(askClaudeResult.ecomm_name)}
            </div>
            <div className={css(styles.claudeMetaRow)}>
              <span className={css(styles.claudeMetaLabel)}>Brand URL:</span>
              {renderUrl(askClaudeResult.brand_url)}
              {renderCopyButton(askClaudeResult.brand_url)}
            </div>
            <div className={css(styles.claudeMetaRow)}>
              <span className={css(styles.claudeMetaLabel)}>Manufacturer Name:</span>
              <span className={css(styles.claudeMetaValue)}>
                {askClaudeResult.manufacturer_name || '—'}
              </span>
              {renderCopyButton(askClaudeResult.manufacturer_name)}
            </div>
            <div className={css(styles.claudeMetaRow)}>
              <span className={css(styles.claudeMetaLabel)}>Manufacturer URL:</span>
              {renderUrl(askClaudeResult.manufacturer_url)}
              {renderCopyButton(askClaudeResult.manufacturer_url)}
            </div>
          </AISuggestionPanel>
        );
      })()}

      <div className="mb-3">
        <FieldLabel label="E-COMM NAME" hint={SAVED_ON_VERIFY_HINT} />
        <Form.Control
          type="text"
          value={eCommName}
          onChange={e => updateField('eCommName', e.target.value)}
          className={css(sharedStyles.textInput, sharedStyles.boldInput)}
          autoCorrect="off"
        />
      </div>
      <div className="mb-3">
        <div className={css(sharedStyles.urlLabelRow)}>
          <FieldLabel
            label="BRAND URL"
            hint={SAVED_ON_VERIFY_HINT}
            warning={isBlank(brandUrl) ? BLANK_WARNING_HINT : null}
          />
          {brandUrl && (
            <a
              href={brandUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={css(sharedStyles.urlOpenIcon)}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          )}
        </div>
        <div className={css(sharedStyles.urlInputWrapper)}>
          <FontAwesomeIcon icon={faQuestionCircle} className={css(sharedStyles.urlInputIcon)} />
          <Form.Control
            type="text"
            value={brandUrl}
            onChange={e => updateField('brandUrl', e.target.value)}
            className={css(sharedStyles.textInput, sharedStyles.urlInput)}
            autoCorrect="off"
          />
        </div>
      </div>
      <div className="mb-3">
        <FieldLabel label="MANUFACTURER" hint={SAVED_ON_VERIFY_HINT} />
        <div className={css(styles.mfrPanelGroup)}>
          <ManufacturerPanel
            active={isNewActive}
            onActivate={() => onActivatePanel(ManufacturerMode.NEW)}
            headerLabel="NEW MANUFACTURER"
            headerExtras={<Badge className={css(styles.noManufacturerIdBadge)}>NEW</Badge>}
          >
            <div className="mb-2">
              <FieldLabel
                label="Manufacturer Name"
                warning={isNewActive && isBlank(newManufacturerName) ? BLANK_WARNING_HINT : null}
              />
              <Form.Control
                type="text"
                value={newManufacturerName}
                onChange={e => updateField('newManufacturerName', e.target.value)}
                onFocus={() => onActivatePanel(ManufacturerMode.NEW)}
                className={css(sharedStyles.textInput)}
                autoCorrect="off"
              />
            </div>
            <div>
              <div className={css(sharedStyles.urlLabelRow)}>
                <FieldLabel
                  label="Manufacturer URL"
                  warning={isNewActive && isBlank(newManufacturerUrl) ? BLANK_WARNING_HINT : null}
                />
                {newManufacturerUrl && (
                  <a
                    href={newManufacturerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className={css(sharedStyles.urlOpenIcon)}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </a>
                )}
              </div>
              <Form.Control
                type="text"
                value={newManufacturerUrl}
                onChange={e => updateField('newManufacturerUrl', e.target.value)}
                onFocus={() => onActivatePanel(ManufacturerMode.NEW)}
                className={css(sharedStyles.textInput)}
                autoCorrect="off"
              />
            </div>
          </ManufacturerPanel>
          <ManufacturerPanel
            active={isExistingActive}
            highlight={manufacturerId != null}
            onActivate={() => onActivatePanel(ManufacturerMode.EXISTING)}
            headerLabel="EXISTING MANUFACTURER"
            headerExtras={
              manufacturerId ? (
                <>
                  {!isExistingActive && (
                    <Badge className={classNames(css(styles.matchFoundBadge), 'mr-2')}>
                      MATCH FOUND
                    </Badge>
                  )}
                  <a
                    href={`/enrich-products/manufacturer-details/${manufacturerId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className={css(sharedStyles.idLink)}
                  >
                    ID {manufacturerId}
                  </a>
                </>
              ) : null
            }
          >
            <ExistingManufacturerFields
              manufacturerId={manufacturerId}
              name={existingManufacturerName}
              url={existingManufacturerUrl}
              savedName={existingManufacturerSavedName}
              savedUrl={existingManufacturerSavedUrl}
              loading={manufacturerUrlLoading}
              filterKey={suggestion.brand.id}
              onPickManufacturer={onPickExistingMfr}
              onChangeName={value => updateField('existingManufacturerName', value)}
              onChangeUrl={value => updateField('existingManufacturerUrl', value)}
              onFieldFocus={() => onActivatePanel(ManufacturerMode.EXISTING)}
              warningVerb="verify"
              disabledByDefault={manufacturerId == null}
              nameBlankWarning={
                isExistingActive && manufacturerId != null && isBlank(existingManufacturerName)
              }
              urlBlankWarning={
                isExistingActive && manufacturerId != null && isBlank(existingManufacturerUrl)
              }
            />
          </ManufacturerPanel>
        </div>
      </div>
      <DistributorsList
        brand={suggestion.brand}
        totalProductCount={suggestion.brand.product_count}
        defaultVisibleCount={5}
      />
      <SampleProductsList brand={suggestion.brand} />
      <div
        className={classNames(
          css(sharedStyles.cardActionBar),
          'd-flex justify-content-between align-items-center',
        )}
      >
        <div className="d-flex align-items-center">
          <Button
            variant="success"
            className={classNames(css(sharedStyles.primaryGreenButton), 'mr-2')}
            onClick={() => updateVerificationStatus(BrandVerificationStatus.VERIFIED)}
            disabled={updatingStatus || manufacturerUrlLoading}
          >
            {updatingStatus ? (
              <Spinner animation="border" role="status" size="sm" />
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="mr-2" />
                Verify
                <span className={css(styles.keyHint)}>V</span>
              </>
            )}
          </Button>
          <Button
            variant="outline-danger"
            className={classNames(css(styles.actionButton), 'mr-2')}
            onClick={() => updateVerificationStatus(BrandVerificationStatus.UNVERIFIABLE)}
            disabled={updatingStatus || manufacturerUrlLoading}
          >
            {updatingStatus ? (
              <Spinner animation="border" role="status" size="sm" />
            ) : (
              <>
                <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                Unverifiable
                <span className={css(styles.keyHint)}>U</span>
              </>
            )}
          </Button>
          <Button
            variant="outline-primary"
            className={classNames(css(styles.actionButton), 'mr-2')}
            onClick={onMergeToVerified}
            disabled={updatingStatus || manufacturerUrlLoading}
          >
            <FontAwesomeIcon icon={faObjectGroup} className="mr-2" />
            Merge to Verified Brand
            <span className={css(styles.keyHint)}>M</span>
          </Button>
          <OverlayTrigger
            overlay={
              <Tooltip>
                Remove this brand from the brand verification queue to handle separately
              </Tooltip>
            }
          >
            <Button
              variant="outline-warning"
              className={css(styles.actionButton)}
              onClick={onBookmark}
              disabled={updatingStatus || manufacturerUrlLoading}
            >
              <FontAwesomeIcon icon={faBookmark} className="mr-2" />
              Bookmark
              <span className={css(styles.keyHint)}>B</span>
            </Button>
          </OverlayTrigger>
        </div>
        <div>
          <Button
            variant="link"
            className={css(styles.skipLink)}
            onClick={onSkip}
            disabled={updatingStatus || manufacturerUrlLoading}
          >
            Skip <span className={css(styles.skipKeyHint)}>S</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  brandLogo: {
    width: 64,
    height: 64,
    objectFit: 'contain',
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    backgroundColor: white,
    flexShrink: 0,
    marginRight: 12,
    cursor: 'pointer',
    ':hover': {
      borderColor: grape,
      boxShadow: `0 0 0 2px ${shadowGrape}`,
    },
  },
  unverifiedBadge: {
    backgroundColor: lightAmber,
    color: darkOrange,
    fontWeight: 700,
    fontSize: '0.7rem',
    padding: '4px 8px',
    borderRadius: 4,
  },
  brandIdSpacing: {
    marginLeft: 12,
  },
  activeProductCount: {
    fontSize: '2rem',
    fontWeight: 800,
    color: squidInk,
    lineHeight: 1,
  },
  activeProductLabel: {
    color: auroMetalSaurus,
    fontSize: '0.75rem',
    marginTop: 2,
  },
  googleLink: {
    color: contentVerifiedBlue,
    fontSize: '0.8rem',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  noManufacturerIdBadge: {
    backgroundColor: lightRed,
    color: bootstrapDanger,
    fontWeight: 700,
    fontSize: '0.7rem',
    padding: '4px 8px',
    borderRadius: 4,
  },
  mfrPanelGroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'stretch',
  },
  matchFoundBadge: {
    backgroundColor: positiveGreen,
    color: white,
    fontWeight: 700,
    fontSize: '0.65rem',
    padding: '3px 7px',
    borderRadius: 4,
    letterSpacing: 0.5,
  },
  claudeMetaRow: {
    fontSize: '0.85rem',
    marginTop: 2,
  },
  claudeMetaLabel: {
    fontWeight: 700,
    color: squidInk,
    marginRight: 6,
  },
  claudeMetaValue: {
    color: squidInk,
  },
  claudeUrl: {
    color: contentVerifiedBlue,
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  copyIcon: {
    color: auroMetalSaurus,
    fontSize: '0.8rem',
    ':hover': {
      color: squidInk,
    },
  },
  actionButton: {
    fontWeight: 600,
    borderRadius: 6,
  },
  keyHint: {
    marginLeft: 8,
    padding: '0 6px',
    border: `1px solid currentColor`,
    borderRadius: 3,
    fontSize: '0.7rem',
    fontWeight: 700,
  },
  skipLink: {
    color: warmGray,
    fontSize: '0.85rem',
    textDecoration: 'none',
    ':hover': {
      color: squidInk,
      textDecoration: 'none',
    },
  },
  skipKeyHint: {
    marginLeft: 6,
    color: warmGray,
    fontSize: '0.75rem',
  },
});

BrandVerifyCard.propTypes = {
  suggestion: PropTypes.object.isRequired,
  formValues: PropTypes.shape({
    brandId: PropTypes.number,
    eCommName: PropTypes.string.isRequired,
    brandUrl: PropTypes.string.isRequired,
    manufacturerMode: PropTypes.oneOf(Object.values(ManufacturerMode)).isRequired,
    manufacturerId: PropTypes.number,
    existingManufacturerName: PropTypes.string.isRequired,
    existingManufacturerUrl: PropTypes.string.isRequired,
    existingManufacturerSavedName: PropTypes.string.isRequired,
    existingManufacturerSavedUrl: PropTypes.string.isRequired,
    newManufacturerName: PropTypes.string.isRequired,
    newManufacturerUrl: PropTypes.string.isRequired,
  }).isRequired,
  updateField: PropTypes.func.isRequired,
  onPickExistingMfr: PropTypes.func.isRequired,
  onActivatePanel: PropTypes.func.isRequired,
  manufacturerUrlLoading: PropTypes.bool,
  updateVerificationStatus: PropTypes.func.isRequired,
  onMergeToVerified: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onBookmark: PropTypes.func.isRequired,
  updatingStatus: PropTypes.bool,
};

export default BrandVerifyCard;
